import { ARTIFACTS_ROOT } from './artifact-links'
import { TAGS_ROOT } from './tag-links'

/** Plant Viewer app root — all tab routes hang off this prefix. */
export const PLANT_ROOT = '/plant'

export type PlantTab =
  | 'equipment'
  | 'processes'
  | 'areas'
  | 'compute'
  | 'queries'
  | 'plc'
  | 'dcs'
  | 'network'
  | 'robotics'
  | 'schedules'
  | 'alarms'
  | 'safety'
  | 'model'

/**
 * React Router path segments relative to `/plant`. Use these in
 * `routes/index.tsx` so the route tree and href builders stay aligned.
 */
export const PLANT_ROUTE_SEGMENTS = {
  equipmentIndex: 'equipment',
  equipmentSite: 'equipment/sites/:siteId',
  equipmentLine: 'equipment/sites/:siteId/lines/:lineId',
  equipmentItem: 'equipment/items/:equipmentId',
  processesIndex: 'processes',
  areasIndex: 'areas',
  areaDetail: 'areas/:areaId',
  computeIndex: 'compute',
  computeHost: 'compute/hosts/:computeId',
  queriesIndex: 'queries',
  queryDetail: 'queries/:queryId',
  plcIndex: 'plc',
  dcsIndex: 'dcs',
  networkIndex: 'network',
  roboticsIndex: 'robotics',
  schedulesIndex: 'schedules',
  schedulesNode: 'schedules/:nodeId',
  alarmsIndex: 'alarms',
  safetyIndex: 'safety',
  safetyGroup: 'safety/groups/:sectionId',
  safetySolver: 'safety/solvers/:solverId',
  safetyFld: 'safety/flds/:fldId',
  safetyPoint: 'safety/points/:pointId',
  safetyDetail: 'safety/:functionId',
  entity: 'entity/:id',
  modelIndex: 'model'
} as const

/** Absolute landing paths for each Plant Viewer tab. */
export const PLANT_HREF = {
  root: PLANT_ROOT,
  equipmentIndex: `${PLANT_ROOT}/equipment`,
  processesIndex: `${PLANT_ROOT}/processes`,
  areasIndex: `${PLANT_ROOT}/areas`,
  computeIndex: `${PLANT_ROOT}/compute`,
  queriesIndex: `${PLANT_ROOT}/queries`,
  plcIndex: `${PLANT_ROOT}/plc`,
  dcsIndex: `${PLANT_ROOT}/dcs`,
  networkIndex: `${PLANT_ROOT}/network`,
  roboticsIndex: `${PLANT_ROOT}/robotics`,
  schedulesIndex: `${PLANT_ROOT}/schedules`,
  alarmsIndex: `${PLANT_ROOT}/alarms`,
  safetyIndex: `${PLANT_ROOT}/safety`,
  entityIndex: `${PLANT_ROOT}/entity`,
  modelIndex: `${PLANT_ROOT}/model`
} as const

/** Query param that selects a process on the Processes tab. */
export const PLANT_PROCESS_QUERY_PARAM = 'process'

/** Query param that scopes session paint to one ingest section. */
export const PLANT_PAINT_SECTION_QUERY_PARAM = 'paintSection'

/** Reserved {@link PLANT_PAINT_SECTION_QUERY_PARAM} value for job-level writes. */
export const PLANT_PAINT_JOB_LEVEL_VALUE = '__job__'

/** Query param controlling session paint visibility. */
export const PLANT_PAINT_QUERY_PARAM = 'paint'

/** Value on {@link PLANT_PAINT_QUERY_PARAM} that disables session paint. */
export const PLANT_PAINT_OFF_VALUE = 'off'

/** Query param that opens a tag-provider panel on the compute host page. */
export const PLANT_COMPUTE_PROVIDER_QUERY_PARAM = 'provider'

/**
 * Query param that pre-selects a single SCADA tag inside the open
 * provider panel. The browser switches to its flat view, pre-fills
 * the search box with the tag name, and highlights the matching row
 * so deep-links from PLC pages land "on" the tag instead of dropping
 * the user at the top of an un-searched provider.
 */
export const PLANT_COMPUTE_TAG_QUERY_PARAM = 'tag'

/** Query param that opens the database query engine on the compute host page. */
export const PLANT_COMPUTE_DB_QUERY_PARAM = 'db'

/** Query param that selects a node on the PLC tab. */
export const PLANT_PLC_NODE_QUERY_PARAM = 'node'

/** Query param that selects a node on the DCS tab. */
export const PLANT_DCS_NODE_QUERY_PARAM = 'node'

/** Query param that selects a node on the Network tab. */
export const PLANT_NETWORK_NODE_QUERY_PARAM = 'node'

/** Query param that selects a node on the Robotics tab. */
export const PLANT_ROBOTICS_NODE_QUERY_PARAM = 'node'

/** Sentinel `:siteId` for stray lines not attached to any `:Site`. */
export const PLANT_STRAY_SITE_SENTINEL = '_strays'

const LINE_ID_SEGMENT = '/line:'

/** Graph viewer highlight query param. */
export const GRAPH_HIGHLIGHT_QUERY_PARAM = 'highlight'

/**
 * React Router decodes path parameters during both path matching and param
 * extraction. Preserve literal percent-encoded data inside canonical ids
 * (for example `%2F` within a tag path) through those two passes.
 */
function encodeRouteParam(value: string): string {
  return encodeURIComponent(value).replace(/%25/gi, '%2525')
}

/** `/plant/equipment` — Equipment tab landing (sites grid). */
export function plantEquipmentIndexHref(): string {
  return PLANT_HREF.equipmentIndex
}

/** `/plant/equipment/sites/:siteId` — single site (lines + feeds flow). */
export function plantSiteHref(siteId: string): string {
  return `${PLANT_HREF.equipmentIndex}/sites/${encodeRouteParam(siteId)}`
}

/**
 * `/plant/equipment/sites/:siteId/lines/:lineId` — line **or** subline detail.
 */
export function plantLineHref(siteId: string, lineId: string): string {
  return `${PLANT_HREF.equipmentIndex}/sites/${encodeRouteParam(siteId)}/lines/${encodeRouteParam(lineId)}`
}

/** `/plant/equipment/items/:equipmentId` — single equipment item. */
export function plantEquipmentItemHref(equipmentId: string): string {
  return `${PLANT_HREF.equipmentIndex}/items/${encodeRouteParam(equipmentId)}`
}

/** `/plant/processes` — Processes tab landing (tree, no selection). */
export function plantProcessesIndexHref(): string {
  return PLANT_HREF.processesIndex
}

/** `/plant/processes?process=<processId>` — selected process (tree + detail). */
export function plantProcessHref(processId: string): string {
  const params = new URLSearchParams()
  params.set(PLANT_PROCESS_QUERY_PARAM, processId)
  return `${PLANT_HREF.processesIndex}?${params.toString()}`
}

/** `/plant/areas` — Areas tab landing (areas grouped by site). */
export function plantAreasIndexHref(): string {
  return PLANT_HREF.areasIndex
}

/** `/plant/areas/:areaId` — single area detail. */
export function plantAreaHref(areaId: string): string {
  return `${PLANT_HREF.areasIndex}/${encodeRouteParam(areaId)}`
}

/** `/plant/compute` — Compute tab landing (host grid). */
export function plantComputeIndexHref(): string {
  return PLANT_HREF.computeIndex
}

/** `/plant/compute/hosts/:computeId` — single compute host. */
export function plantComputeHostHref(computeId: string): string {
  return `${PLANT_HREF.computeIndex}/hosts/${encodeRouteParam(computeId)}`
}

/** `/plant/compute/hosts/:computeId?provider=:providerId` — host with tag provider panel open. */
export function plantComputeTagProviderHref(computeId: string, providerId: string): string {
  const params = new URLSearchParams()
  params.set(PLANT_COMPUTE_PROVIDER_QUERY_PARAM, providerId)
  return `${plantComputeHostHref(computeId)}?${params.toString()}`
}

/**
 * `/plant/compute/hosts/:computeId?provider=:providerId&tag=:tagName` —
 * deep-link to a single SCADA tag inside its provider panel.
 *
 * Pre-fills the provider browser's search box with `tagName` (which
 * is what the search column matches on) and switches the panel to
 * flat view so the row is immediately visible. Used by the PLC page's
 * SCADA-tag chips and any other cross-layer link that wants to land
 * the user "on" a specific tag instead of just on the host.
 */
export function plantComputeTagHref(
  computeId: string,
  providerId: string,
  tagName: string
): string {
  const params = new URLSearchParams()
  params.set(PLANT_COMPUTE_PROVIDER_QUERY_PARAM, providerId)
  params.set(PLANT_COMPUTE_TAG_QUERY_PARAM, tagName)
  return `${plantComputeHostHref(computeId)}?${params.toString()}`
}

/** `/plant/queries` — Queries tab landing (NamedQuery + query-Tag list). */
export function plantQueriesIndexHref(): string {
  return PLANT_HREF.queriesIndex
}

/** `/plant/queries/:queryId` — single query detail (NamedQuery or query Tag). */
export function plantQueryHref(queryId: string): string {
  return `${PLANT_HREF.queriesIndex}/${encodeRouteParam(queryId)}`
}

/** `/plant/plc` — PLC tab landing (Controller/Program/Routine/AOI tree, no selection). */
export function plantPlcIndexHref(): string {
  return PLANT_HREF.plcIndex
}

/** `/plant/dcs` — DCS tab landing (DcsController/Cee/ControlModule tree, no selection). */
export function plantDcsIndexHref(): string {
  return PLANT_HREF.dcsIndex
}

/** `/plant/network` — Network tab landing (Asset/NetworkSegment/NetworkDevice tree). */
export function plantNetworkIndexHref(): string {
  return PLANT_HREF.networkIndex
}

/** `/plant/robotics` — Robotics tab landing (RobotController/Arm/Job tree). */
export function plantRoboticsIndexHref(): string {
  return PLANT_HREF.roboticsIndex
}

/** `/plant/schedules` — Schedules tab landing (schedule profiles + holidays). */
export function plantSchedulesIndexHref(): string {
  return PLANT_HREF.schedulesIndex
}

/** `/plant/schedules/:nodeId` — schedule profile or holiday detail. */
export function plantSchedulesNodeHref(nodeId: string): string {
  return `${PLANT_HREF.schedulesIndex}/${encodeRouteParam(nodeId)}`
}

/** `/plant/alarms` — Alarms tab landing (grouped by UDTDefinition > UDTInstance > tag path). */
export function plantAlarmsIndexHref(): string {
  return PLANT_HREF.alarmsIndex
}

/** `/plant/safety` — compact SRS function-group and Safety Manager landing. */
export function plantSafetyIndexHref(): string {
  return PLANT_HREF.safetyIndex
}

/** `/plant/safety/groups/:sectionId` — site-scoped SRS function group. */
export function plantSrsFunctionGroupHref(sectionId: string, search?: string): string {
  const href = `${PLANT_HREF.safetyIndex}/groups/${encodeRouteParam(sectionId)}`
  if (!search?.trim()) return href
  const params = new URLSearchParams()
  params.set('q', search.trim())
  return `${href}?${params.toString()}`
}

/** `/plant/safety/:functionId` — single protective-function detail. */
export function plantProtectiveFunctionHref(functionId: string): string {
  return `${PLANT_HREF.safetyIndex}/${encodeRouteParam(functionId)}`
}

/** `/plant/safety/solvers/:solverId` — logic-solver detail (FLDs + solved SIFs). */
export function plantLogicSolverHref(solverId: string): string {
  return `${PLANT_HREF.safetyIndex}/solvers/${encodeRouteParam(solverId)}`
}

/** `/plant/safety/flds/:fldId` — Function Logic Diagram detail (points + implementing SIFs). */
export function plantFunctionLogicDiagramHref(fldId: string): string {
  return `${PLANT_HREF.safetyIndex}/flds/${encodeRouteParam(fldId)}`
}

/** `/plant/safety/points/:pointId` — Safety Manager point detail. */
export function plantSafetyPointHref(pointId: string): string {
  return `${PLANT_HREF.safetyIndex}/points/${encodeRouteParam(pointId)}`
}

/** `/plant/entity/:id` — universal entity resolver and generic detail. */
export function plantEntityHref(id: string): string {
  return `${PLANT_HREF.entityIndex}/${encodeRouteParam(id)}`
}

/** `/plant/model` — internal schema-driven model browser. */
export function plantModelIndexHref(): string {
  return PLANT_HREF.modelIndex
}

/** `/plant/plc?node=<nodeId>` — selected PLC node (tree + detail). */
export function plantPlcNodeHref(nodeId: string): string {
  const params = new URLSearchParams()
  params.set(PLANT_PLC_NODE_QUERY_PARAM, nodeId)
  return `${PLANT_HREF.plcIndex}?${params.toString()}`
}

/** `/plant/dcs?node=<nodeId>` — selected DCS node (tree + detail). */
export function plantDcsNodeHref(nodeId: string): string {
  const params = new URLSearchParams()
  params.set(PLANT_DCS_NODE_QUERY_PARAM, nodeId)
  return `${PLANT_HREF.dcsIndex}?${params.toString()}`
}

/** `/plant/network?node=<nodeId>` — selected Network node (tree + detail). */
export function plantNetworkNodeHref(nodeId: string): string {
  const params = new URLSearchParams()
  params.set(PLANT_NETWORK_NODE_QUERY_PARAM, nodeId)
  return `${PLANT_HREF.networkIndex}?${params.toString()}`
}

/** `/plant/robotics?node=<nodeId>` — selected Robotics node (tree + detail). */
export function plantRoboticsNodeHref(nodeId: string): string {
  const params = new URLSearchParams()
  params.set(PLANT_ROBOTICS_NODE_QUERY_PARAM, nodeId)
  return `${PLANT_HREF.roboticsIndex}?${params.toString()}`
}

/** `/plant/equipment/sites/_strays/lines/:lineId` — stray line detail. */
export function plantStrayLineHref(lineId: string): string {
  return `${PLANT_HREF.equipmentIndex}/sites/${PLANT_STRAY_SITE_SENTINEL}/lines/${encodeRouteParam(lineId)}`
}

/**
 * Extract the owning site id from a canonical line URN
 * (`urn:org:…/site:…/line:…`). Returns null for legacy ids without that
 * segment.
 */
export function siteIdFromLineId(lineId: string): string | null {
  const idx = lineId.lastIndexOf(LINE_ID_SEGMENT)
  if (idx <= 0) return null
  return lineId.slice(0, idx)
}

/** Plant Viewer line detail — uses the parsed site id or the stray sentinel. */
export function plantLineHrefFromLineId(lineId: string): string {
  const siteId = siteIdFromLineId(lineId)
  if (siteId) return plantLineHref(siteId, lineId)
  return plantStrayLineHref(lineId)
}

/** `/graph?highlight=:id` — graph view with a node highlighted. */
export function graphHighlightHref(nodeId: string): string {
  const params = new URLSearchParams()
  params.set(GRAPH_HIGHLIGHT_QUERY_PARAM, nodeId)
  return `/graph?${params.toString()}`
}

/** True when `href` is a supported in-app route, not an external URL. */
export function isInternalAppHref(href: string): boolean {
  return (
    href.startsWith(`${PLANT_ROOT}/`) ||
    href.startsWith('/graph?') ||
    href === TAGS_ROOT ||
    href.startsWith(`${TAGS_ROOT}?`) ||
    href === '/documents' ||
    href.startsWith('/documents/') ||
    href === '/dashboards' ||
    href.startsWith('/dashboards/') ||
    href.startsWith(`${ARTIFACTS_ROOT}/`) ||
    /^\/ingest\/jobs\/[^/?#]+\/source-document(?:[?#]|$)/.test(href)
  )
}

/**
 * Infer the active Plant Viewer tab from a pathname. Defaults to equipment
 * (Factory) for unknown paths and for `/plant/entity/*` so shared entity
 * detail pages stay under the Factory chrome. Model is only the browser.
 */
export function resolvePlantTab(pathname: string): PlantTab {
  if (
    pathname === PLANT_HREF.processesIndex ||
    pathname.startsWith(`${PLANT_HREF.processesIndex}?`)
  ) {
    return 'processes'
  }
  if (pathname === PLANT_HREF.areasIndex || pathname.startsWith(`${PLANT_HREF.areasIndex}/`)) {
    return 'areas'
  }
  if (pathname === PLANT_HREF.computeIndex || pathname.startsWith(`${PLANT_HREF.computeIndex}/`)) {
    return 'compute'
  }
  if (pathname === PLANT_HREF.queriesIndex || pathname.startsWith(`${PLANT_HREF.queriesIndex}/`)) {
    return 'queries'
  }
  if (pathname === PLANT_HREF.plcIndex || pathname.startsWith(`${PLANT_HREF.plcIndex}?`)) {
    return 'plc'
  }
  if (pathname === PLANT_HREF.dcsIndex || pathname.startsWith(`${PLANT_HREF.dcsIndex}?`)) {
    return 'dcs'
  }
  if (pathname === PLANT_HREF.networkIndex || pathname.startsWith(`${PLANT_HREF.networkIndex}?`)) {
    return 'network'
  }
  if (
    pathname === PLANT_HREF.roboticsIndex ||
    pathname.startsWith(`${PLANT_HREF.roboticsIndex}?`)
  ) {
    return 'robotics'
  }
  if (
    pathname === PLANT_HREF.schedulesIndex ||
    pathname.startsWith(`${PLANT_HREF.schedulesIndex}?`) ||
    pathname.startsWith(`${PLANT_HREF.schedulesIndex}/`)
  ) {
    return 'schedules'
  }
  if (
    pathname === PLANT_HREF.alarmsIndex ||
    pathname.startsWith(`${PLANT_HREF.alarmsIndex}/`) ||
    pathname.startsWith(`${PLANT_HREF.alarmsIndex}?`)
  ) {
    return 'alarms'
  }
  if (
    pathname === PLANT_HREF.safetyIndex ||
    pathname.startsWith(`${PLANT_HREF.safetyIndex}/`) ||
    pathname.startsWith(`${PLANT_HREF.safetyIndex}?`)
  ) {
    return 'safety'
  }
  // Entity resolver is a shared Factory detail surface (Procedure, Utility, …),
  // not the Model browser — keep Factory tab highlighted.
  if (pathname.startsWith(`${PLANT_HREF.entityIndex}/`)) {
    return 'equipment'
  }
  if (pathname === PLANT_HREF.modelIndex || pathname.startsWith(`${PLANT_HREF.modelIndex}/`)) {
    return 'model'
  }
  return 'equipment'
}

/** Restore a canonical id from a React Router path parameter. */
export function decodeRouteParam(value: string): string {
  if (value.length === 0) return value
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}
